/**
 * Utility functions for Pre-Anesthesia Assessment Form & PAC Clearance calculations.
 */

/**
 * Calculates BMI given weight in kg and height in cm.
 * Returns formatted string to 1 decimal place or null if invalid.
 */
export function calculateBmi(weightKg, heightCm) {
  const w = parseFloat(weightKg);
  const h = parseFloat(heightCm);
  if (w > 0 && h > 0) {
    const hM = h / 100;
    return (w / (hM * hM)).toFixed(1);
  }
  return null;
}

/**
 * Returns the BMI clinical category and badge color.
 */
export function getBmiCategory(bmi) {
  const val = parseFloat(bmi);
  if (!val || isNaN(val)) return { label: "Unknown", color: "default" };
  if (val < 18.5) return { label: "Underweight (<18.5)", color: "warning" };
  if (val <= 24.9) return { label: "Normal (18.5-24.9)", color: "success" };
  if (val <= 29.9) return { label: "Overweight (25-29.9)", color: "warning" };
  return { label: "Obese (≥30)", color: "error" };
}

/**
 * Dental tooth state cycling: NORMAL -> MISSING (*) -> LOOSE (O) -> NORMAL
 */
export function getNextToothState(currentState) {
  if (currentState === "MISSING") return "LOOSE";
  if (currentState === "LOOSE") return "NORMAL";
  return "MISSING";
}

/**
 * Formats ASA classification with emergency modifier: e.g. "ASA II E"
 */
export function formatAsaDisplay(asaClass, isEmergency) {
  if (!asaClass) return "Not assessed";
  return `ASA ${asaClass}${isEmergency ? " E" : ""}`;
}

/**
 * Formats NPPA DPCO 2013 price cap compliance metadata for display.
 */
export function formatNppaCompliance(compliance) {
  if (!compliance) return null;
  const isComp = compliance.is_compliant !== false && compliance.status_badge !== "EXCEEDS_CEILING";
  return {
    isCompliant: isComp,
    badgeText: isComp
      ? "✓ NPPA DPCO 2013 Price Cap Compliant"
      : "⚠ Exceeds NPPA DPCO 2013 Price Ceiling",
    badgeColor: isComp ? "success" : "warning",
    categoryLabel: compliance.category_label || "Statutory Ceiling Order S.O. 2668(E)",
    maxCapFormatted: `₹${Number(compliance.max_notified_ceiling || 0).toLocaleString("en-IN")}`,
    statutoryOrder: compliance.statutory_order || "NPPA DPCO 2013 Order S.O. 2668(E)",
  };
}

